import { z } from 'zod';
import type { IStage } from '.';

export const BouwHetLandschapDataSchema = z.object({}).default({});

export type Stage =
	| IStage
	| 'answer-questions'
	| 'wait-for-theme-and-area-presenting'
	| 'theme-and-area-presenting'
	| 'wait-for-rounds'
	| 'start-presenting'
	| 'play-presenting'
	| 'play-rounds'
	| 'prepare-pitches'
	| 'wait-for-pitch-battle'
	| 'pitch-battles'
	| 'pitch-review'
	| 'wait-for-start-pitches'
	| 'wait-for-pitch';

export type BouwHetLandschapAfdruk = {
	number: number;
	questions: [number, number, number];
	points: number;
};

export type BouwHetLandschapTeamData = {
	vingerafdruk: [BouwHetLandschapAfdruk, BouwHetLandschapAfdruk, BouwHetLandschapAfdruk];
};

export const BouwHetLandschapThemeSchema = z.object({
	name: z.string(),
});
export type BouwHetLandschapTheme = z.infer<typeof BouwHetLandschapThemeSchema>;

export const BouwHetLandschapPresetSchema = z.object({
	presetName: z.string().default('No name'),
	theme1: BouwHetLandschapThemeSchema.default({ name: '' }),
	theme2: BouwHetLandschapThemeSchema.default({ name: '' }),
	theme3: BouwHetLandschapThemeSchema.default({ name: '' }),
	theme4: BouwHetLandschapThemeSchema.default({ name: '' }),
});
export type BouwHetLandschapPreset = z.infer<typeof BouwHetLandschapPresetSchema>;

export const BouwHetLandschapPitchPart = z.object({
	answer: z.string(),
	details: z.string(),
	playerId: z.string(),
});

export type BouwHetLandschapPitchAnswer = {
	id: string;
	answer: string;
};

export type BouwHetLandschapPlayerState = {
	id: string;
	name: string;
	color: string;
	score: number;
	active: boolean;
	answers: string[];
	lastScreen: BouwHetLandschapUpdate;
};

export type BouwHetLandschapPitchPart = {
	answer: string;
	details: string;
	playerId: string;
};

export type BouwHetLandschapPitch = {
	theme: string;
	playerId?: string;
	area?: BouwHetLandschapPitchPart;
	characters?: BouwHetLandschapPitchPart;
	conflict?: BouwHetLandschapPitchPart;
	solution?: BouwHetLandschapPitchPart;
	pitch?: string;
};

export type BouwHetLandschapPreparePitch = BouwHetLandschapPitch & {
	id: string;
};

export const BouwHetLandschapAnswerSchema = z.object({
	playerId: z.string(),
	answer: z.string(),
	details: z.string(),
	pitch: z.string().optional(),
});

export type BouwHetLandschapAnswer = z.infer<typeof BouwHetLandschapAnswerSchema>;

export const BouwHetLandschapQuestionSchema = z.object({
	id: z.string(),
	question: z.string(),
});

export type BouwHetLandschapQuestion = z.infer<typeof BouwHetLandschapQuestionSchema>;

export type BouwHetLandschapState = {
	stage: Stage;
	roundIndex: number;
	questionRoundIndex: number;
	pitchRoundIndex: number;
	lastRound: boolean;
	players: BouwHetLandschapPlayerState[];
	currentPlayerIndex: number;
	game: BouwHetLandschapPreset;
	// answers?: Record<string, BouwHetLandschapPitch>;
	answers?: Record<string, BouwHetLandschapAnswer>;
	answersOnBoard: { questionNumber: number; playerIndex: number }[];
};

export const BouwHetLandschapClientSchema = z.array(
	z.object({
		number: z.number(),
		points: z.number(),
		questions: z.array(
			z.object({
				number: z.number(),
				text: z.string(),
			}),
		),
	}),
);
export type BouwHetLandschapClient = z.infer<typeof BouwHetLandschapClientSchema>;

export const Participants = z.object({
	id: z.string(),
	name: z.string(),
	color: z.string(),
});

const PitchSchema = z.object({
	id: z.string(),
	theme: z.string(),
	area: BouwHetLandschapAnswerSchema.optional(),
	characters: BouwHetLandschapAnswerSchema.optional(),
	conflict: BouwHetLandschapAnswerSchema.optional(),
	solution: BouwHetLandschapAnswerSchema.optional(),
	pitch: z.string().optional(),
});

export type BouwHetLandschapPitchNew = z.infer<typeof PitchSchema>;

export const BouwHetLandschapUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({
		kind: z.literal('theme-and-area-presenting-screen'),
		questions: z.array(BouwHetLandschapQuestionSchema),
		answers: z.record(z.string(), BouwHetLandschapAnswerSchema),
	}),
	z.object({
		kind: z.literal('prepare-pitch-screen'),
		topics: z.array(PitchSchema),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-questions-screen'),
		round: z.number(),
		name: z.string(),
		questions: z.array(BouwHetLandschapQuestionSchema),
		answers: z.record(z.string(), BouwHetLandschapAnswerSchema),

		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-wait-screen'),
		title: z.string(),
		message: z.string(),
	}),
	z.object({
		kind: z.literal('show-choose-diceroll-screen'),
	}),

	z.object({
		kind: z.literal('show-choose-topic-screen'),
		topics: z.array(
			z.object({
				key: z.string(),
				name: z.string(),
				question: z.string(),
				answer: BouwHetLandschapAnswerSchema.optional(),
			}),
		),
		unavailableTopics: z.array(z.string()),
	}),
	z.object({
		kind: z.literal('show-topic-screen'),
		topic: z.object({
			key: z.string(),
			name: z.string(),
			question: z.string(),
			answer: BouwHetLandschapAnswerSchema.optional(),
		}),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),
	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			name: z.string(),
			color: z.string(),
			score: z.number(),
		}),
	}),
	z.object({
		kind: z.literal('show-pitch-screen'),
		pitch: PitchSchema,
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-review-pitch-screen'),
		pitches: z.array(PitchSchema),
		participants: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
]);
export type BouwHetLandschapUpdate = z.infer<typeof BouwHetLandschapUpdateSchema>;

export const BouwHetLandschapMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('end-questions') }),
	z.object({ kind: z.literal('end-pitch-prepare') }),
	z.object({ kind: z.literal('start-pitch-battles') }),
	z.object({ kind: z.literal('start-pitch') }),
	z.object({ kind: z.literal('start-theme-and-area-presenting') }),
	z.object({ kind: z.literal('start-rounds') }),
	z.object({ kind: z.literal('set-is-last-round'), lastRound: z.boolean() }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),
	z.object({ kind: z.literal('player-finished-street'), playerId: z.string(), points: z.number() }),
	z.object({ kind: z.literal('finish-presenting') }),
	z.object({ kind: z.literal('pitch-battles') }),
	z.object({ kind: z.literal('end-pitch') }),
	z.object({ kind: z.literal('end-pitch-round') }),

	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({
		kind: z.literal('submit-answer'),
		playerId: z.string(),

		answers: z.record(
			z.string(),
			z.object({
				answer: z.string(),
				details: z.string(),
			}),
		),

		// answers: z.array(
		// 	z.object({
		// 		name: z.string(),
		// 		questionNumber: z.number(),
		// 		id: z.string(),
		// 		answer: z.string(),
		// 		details: z.string(),
		// 	}),
		// ),
	}),
	z.object({
		kind: z.literal('submit-pitch'),
		playerId: z.string(),
		pitches: z.array(
			z.object({
				id: z.string(),
				answer: z.string(),
			}),
		),
	}),
	z.object({
		kind: z.literal('review-pitch'),
		playerId: z.string(),
	}),
	z.object({
		kind: z.literal('select-topic'),
		playerId: z.string(),
		topic: z.object({
			key: z.string(),
			name: z.string(),
			question: z.string(),
			answer: BouwHetLandschapAnswerSchema.optional(),
		}),
	}),
	z.object({ kind: z.literal('submit-topic'), playerId: z.string() }),
	z.object({ kind: z.literal('submit-answer-to-present'), playerId: z.string(), questionNumber: z.number() }),
]);

export type BouwHetLandschapMessage = z.infer<typeof BouwHetLandschapMessageSchema>;
